import React from "react";
import { ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import PropTypes from "prop-types";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2196F3",
      light: "#2196F30A",
      contrastText: "#FFFFFF"
    },
    background: {
      paper: "#FFFFFF",
      light: "#F9FAFC",
      contrastText: "#000000DE"
    },
    text: {
      primary: "#000000DE",
      secondary: "#0000008F"
    }
  }
});

const themeWithTypography = createTheme(theme, {
  typography: {
    fontFamily: ["Roboto", "sans-serif"].join(","),
    body1: {
      fontSize: "14px",
      fontWeight: 400
    },
    body2: {
      fontSize: "14px",
      fontWeight: 500
    },
    subtitle2: {
      fontSize: "14px",
      fontWeight: 500,
      color: theme.palette.text.primary
    },
    menuItem: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: theme.palette.text.primary
    },
    menuItemSecondary: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: theme.palette.text.secondary
    }
  },
  custom: {
    menu: {
      fontSize: 16,
      fontSizePx: "16px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: theme.palette.text.primary,
      colorSecondary: theme.palette.text.secondary,
      icon: "#7A7A7A"
    }
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none"
          },
          "&:active": {
            boxShadow: "none"
          },
          "&.Mui-focusVisible": {
            boxShadow: "none"
          }
        }
      }
    },
    MuiMenuItem: {
      variants: [
        {
          props: { variant: "sideMenu" },
          style: {
            backgroundColor: "transparent",
            color: "#00000061",
            fontSize: "16px",
            padding: "15px 25px",
            borderBottom: "1px solid #0000001F",
            boxSizing: "border-box",
            "&:hover": {
              backgroundColor: "transparent"
            },
            "&.Mui-selected, &.Mui-selected:hover": {
              position: "relative",
              borderRadius: "8px",
              backgroundColor: "#2196F3",
              color: "#FFFFFF",
              borderBottom: "1px solid #0000001F",
              fontWeight: 700,
              margin: "8px 0",
              padding: "10px 20px"
            },
            "&.Mui-selected::after, &.Mui-selected:hover::after": {
              content: "\"\"",
              position: "absolute",
              bottom: "-8px",
              left: 0,
              right: 0,
              borderBottom: "1px solid #0000001F",
              pointerEvents: "none"
            }
          }
        }
      ],
      styleOverrides: {
        root: {
          padding: "10px 20px",
          fontSize: "16px",
          lineHeight: 1.4,
          "&.Mui-selected": {
            backgroundColor: "#2196F314",
            "&:hover": {
              backgroundColor: "#2196F314",
              opacity: 0.9
            }
          },
          "&:hover": {
            backgroundColor: "#F9FAFC"
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          // Prevents content below from shifting when the error text appears/disappears
          position: "absolute",
          top: "100%", // Positions the helper text right below the input field
          marginTop: "4px" // Optional: adds a small space between the field and the text
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          "&:hover, &.Mui-focused": {
            backgroundColor: "#2196F314 !important"
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          "& .MuiIconButton-root .MuiSvgIcon-root": {
            width: 22,
            height: 22
          }
        }
      }
    }
  }
});

const CustomTheme = ({ children }) => (
  <ThemeProvider theme={themeWithTypography}>{children}</ThemeProvider>
);

CustomTheme.propTypes = {
  children: PropTypes.node.isRequired
};

export default CustomTheme;
